import logging from '@wr/logging';
import eventIds from '../../event-ids';
import {
    optileInitSucceeded,
    optileSubmittingSucceeded,
    optileSubmittingLoading,
    optileSubmittingError,
    optilePaymentAutorise
} from './action-creators';
import {xhrRequestPaymentAutorise} from './xhrRequests';
import {setLanguageChooserDisabled} from '../common/action-creators';
import {
    genericErrorHasOccurred
} from '../generic-error/action-creators';
import {redirectTo} from '../../utils/helpers';
import localiseUrl from '../../utils/localiseUrl';
import config from '../../config';

export const init = (sessionId, languageCode) => dispatch => {
    const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
    const baseUrl = window.location.hostname === 'localhost' ?
        `${config.optile.api}/${languageCode}/sessions/${sessionId}` : window.location.href;
    const url = `${baseUrl}/init`;

    logger.info('Retrieving Optile init data', {
        eventId: eventIds['Retrieving Optile init data'],
        sessionId
    });

    if (config.features.optileSAQA.enabled) {
        dispatch(setLanguageChooserDisabled(true));
    }

    return fetch(url, {
        credentials: 'same-origin',
        cache: 'no-cache',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: 0
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            return response.json();
        })
        .then(data => {
            dispatch(optileInitSucceeded(data));
        })
        .catch(error => {
            logger.error(
                `Error retrieving Optile init data: ${error.message}`,
                {
                    error,
                    eventId: eventIds['Error retrieving Optile init data'],
                    sessionId
                }
            );

            return dispatch(genericErrorHasOccurred(true));
        });
};

export const pay = ({
    languageCode,
    encryptedAccount,
    cardType: network,
    userAgent,
    ipAddress,
    remember,
    cardNumberMasked,
    expirationMonth,
    expirationYear,
    availableLanguageCodes
}) => dispatch => {
    dispatch(optileSubmittingLoading(true));
    dispatch(optileSubmittingError(''));

    const baseUrl = window.location.href;
    const apiUrl = `${baseUrl}/pay`;
    return fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify({
            encryptedAccount,
            network,
            languageCode,
            userDetails: {
                userAgent,
                ipAddress
            },
            cardNumberMasked,
            expirationMonth,
            expirationYear,
            remember
        })
    })
        .then(response => {
            if (!response.ok) {
                throw Error(response.statusText);
            }
            return response.json();
        })
        .then(data => {
            dispatch(optileSubmittingSucceeded(data));

            if (data.redirect && data.redirect.url) {
                const {url, method, parameters} = data.redirect;
                const localisedUrl = localiseUrl(url, languageCode, availableLanguageCodes);
                redirectTo(localisedUrl, {method, parameters});
                return data;
            }

            dispatch(optileSubmittingError(data.message));
            dispatch(optileSubmittingLoading(false));
            return data;
        })
        .catch(error => {
            const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);
            logger.error(
                `Error retrieving Optile submit data: ${error.message}`,
                {
                    error,
                    eventId: eventIds['Error retrieving Optile submit data']
                }
            );
            return dispatch(genericErrorHasOccurred(true));
        });
};

export const paymentAutorise = (
    sessionId,
    languageCode,
    availableLanguageCodes
) => dispatch => {
    const logger = logging.createLogger(config.app.name, config.logger.logServerUrl);

    if (window.location.hostname === 'localhost') {
        return xhrRequestPaymentAutorise(sessionId, languageCode, availableLanguageCodes, dispatch);
    }

    const baseUrl = window.location.origin;
    const apiUrl = `${baseUrl}/${languageCode}/payment-providers/opt/sessions/${sessionId}/bank/authorise`;

    logger.info('Retrieving Optile bank payment data', {
        eventId: eventIds['Retrieving Optile bank payment data'],
        sessionId
    });

    return fetch(apiUrl, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
    })
        .then(response => {
            if (!response.ok) {
                throw Error(response.statusText);
            }
            return response.json();
        })
        .then(data => {
            dispatch(setLanguageChooserDisabled(true));
            dispatch(optilePaymentAutorise(data));

            if (data.redirectUrl) {
                return redirectTo(data.redirectUrl, {method: 'GET', parameters: null});
            }

            if (data.redirect?.url) {
                const {url, method, parameters} = data.redirect;
                const localisedUrl = localiseUrl(url, languageCode, availableLanguageCodes);
                return redirectTo(localisedUrl, {method, parameters});
            }

            dispatch(genericErrorHasOccurred(true));
            return data;
        })
        .catch(error => {
            logger.error(
                `Error retrieving Optile bank payment data: ${error.message}`,
                {
                    error,
                    eventId: eventIds['Error retrieving Optile bank payment data'],
                    sessionId
                }
            );
            return dispatch(genericErrorHasOccurred(true));
        });
};
